import { PinpointStep } from 'models/Pinpoint';
import { HttpMethod } from 'types/SiteAdministration';
import { parseFileUploadSizeLimit } from 'shared/env';

/**
 * Default backend route prefix for endpoints that do not carry their own
 * `/secure` or `/unsecure` segment. Centralized so every API module resolves
 * the same value from `VITE_APP_UNSECURE_ROUTE` instead of duplicating the
 * fallback literal.
 */
export const UNSECURE_ROUTE = import.meta.env.VITE_APP_UNSECURE_ROUTE ?? 'secure';

const { timeZone } = Intl.DateTimeFormat().resolvedOptions();

// ENVIRONMENT VARIABLES
export const TIMEZONE = timeZone;
export const APP_NAME = import.meta.env.VITE_APP_APP_NAME || 'Vantage™';
export const BASE_URL = import.meta.env.VITE_APP_EXPRESS_URL;
export const BASE_URL_V2 = import.meta.env.VITE_APP_NESTJS_BASE_URL;
/** When true, use new deal APIs on Nest (BASE_URL_V2): contract sections, contract BOM, lov/units-of-measure, etc. */
export const USE_NEW_DEAL_API = import.meta.env.VITE_APP_USE_NEW_DEAL_API === 'true';
export const ENABLE_SD_PRODUCTS = import.meta.env.VITE_APP_ENABLE_SD_PRODUCTS === 'true';
export const ENABLE_WEBINARS = import.meta.env.VITE_APP_WEBINAR_ENABLED === 'true';
export const ANONYMOUS_CLIENT_NAME = import.meta.env.VITE_APP_ANONYMOUS_CLIENT_NAME || 'Acme Industries';
export const ANONYMOUS_CLIENT_SHORT_NAME = import.meta.env.VITE_APP_ANONYMOUS_CLIENT_SHORT_NAME || 'AW';
export const ANONYMOUS_CLIENT_EMAIL = import.meta.env.VITE_APP_ANONYMOUS_CLIENT_EMAIL || 'kschmidt@acmewidgets.com';
export const ANONYMOUS_CLIENT_USER = import.meta.env.VITE_APP_ANONYMOUS_CLIENT_USER || 'Kelly Schmidt';
export const ENV = import.meta.env.VITE_APP_ENV;
export const HANDS_ON_TABLE_LICENSE = import.meta.env.VITE_APP_HANDS_ON_TABLE_LICENSE || '';
export const CLEAR_SITE_DATA_FAQ =
  import.meta.env.VITE_APP_CLEAR_SITE_DATA_FAQ ||
  'https://npi-vantage.freshdesk.com/en/support/solutions/articles/153000189754-clearing-vantage-site-data';
export const AUTH0_TOKEN_URL = import.meta.env.VITE_APP_AUTH0_TOKEN_URL;
export const APRYSE_KEY = import.meta.env.VITE_APP_APRYSE_KEY || '';
export const LOGROCKET_APP_ID = import.meta.env.VITE_APP_LOGROCKET || '';

/**
 * Join a base URL with a path slug while preserving the base URL's path prefix.
 *
 * `new URL(slug, base)` cannot be used here: a leading-slash slug is treated as an
 * absolute path and replaces the base URL's path entirely, which silently drops the
 * `/v2` API prefix on environments where `BASE_URL_V2` carries one (e.g. QA's
 * `https://api-qa.npi-digital.com/v2`). Plain concatenation with slash normalization
 * keeps the prefix regardless of trailing/leading slash conventions in the env values.
 * @param {string} base - Base URL, optionally with a path prefix (e.g. ".../v2").
 * @param {string} slug - Path slug to append (with or without a leading slash).
 * @returns {string} The joined URL with the base path prefix preserved.
 */
const joinAgentUrl = (base: string, slug: string): string =>
  `${(base || '').replace(/\/$/, '')}/${(slug || '').replace(/^\//, '')}`;

const AGENT_RUNTIME_URL_SLUG = import.meta.env.VITE_APP_AGENT_RUNTIME_URL || '/agent/runs/stream';
export const AGENT_RUNTIME_URL = joinAgentUrl(BASE_URL_V2, AGENT_RUNTIME_URL_SLUG);

export const AGENT_ATTACHMENTS_URL_SLUG = import.meta.env.VITE_APP_AGENT_ATTACHMENTS_URL || '/api/agent/attachments';
export const AGENT_ATTACHMENTS_URL = joinAgentUrl(BASE_URL_V2, AGENT_ATTACHMENTS_URL_SLUG);

export const VANTAGE_AGENT_ID = import.meta.env.VITE_APP_VANTAGE_AGENT_ID || 'vantage-advisor';
export const LOGO_BASE_URL = import.meta.env.VITE_APP_LOGO_BASE_URL || 'https://img.logo.dev';
export const LOGO_DEV_TOKEN = import.meta.env.VITE_APP_LOGO_DEV_TOKEN || '';

/**
 * Get the logo URL for a given domain using Logo.dev API
 * @param {string} domain - The domain to get the logo for (e.g., 'stripe.com')
 * @returns {string} The full logo URL with token parameter
 */
export const getLogoUrl = (domain: string): string => {
  if (!domain) return '';
  return `${LOGO_BASE_URL}/${domain}?token=${LOGO_DEV_TOKEN}`;
};

/**
 * Normalize a domain or website URL into a hostname for logo lookups.
 * @param {string | null | undefined} domainOrWebsite - Raw domain/URL value (e.g., "stripe.com" or "https://stripe.com/about")
 * @returns {string | undefined} Normalized hostname when valid, otherwise undefined
 */
export const normalizeLogoDomain = (domainOrWebsite?: string | null): string | undefined => {
  if (!domainOrWebsite) {
    return undefined;
  }

  const trimmedValue = domainOrWebsite.trim();
  if (!trimmedValue) {
    return undefined;
  }

  const valueWithProtocol = /^https?:\/\//i.test(trimmedValue) ? trimmedValue : `https://${trimmedValue}`;

  try {
    const parsedUrl = new URL(valueWithProtocol);
    return parsedUrl.hostname || undefined;
  } catch {
    const withoutProtocol = trimmedValue.replace(/^https?:\/\//i, '');
    const hostname = withoutProtocol.split('/')[0]?.split('?')[0]?.split('#')[0]?.split(':')[0]?.trim();
    return hostname || undefined;
  }
};

// Additional Constants
export const FILE_UPLOAD_SIZE_LIMIT = parseFileUploadSizeLimit(import.meta.env.VITE_APP_FILE_UPLOAD_SIZE_LIMIT);
export const BUGSNAG_APIKEY = import.meta.env.VITE_APP_BUGSNAG_APIKEY || '';
// Userflow token — empty when unset so Userflow init is skipped (no QA-token fallback)
export const USERFLOW_TOKEN = import.meta.env.VITE_APP_USERFLOW_TOKEN || '';
export const ADD_BULK_DEAL_TEMPLATE =
  import.meta.env.VITE_APP_ADD_BULK_DEAL_TEMPLATE ||
  'https://s3.us-east-2.amazonaws.com/public.npi-digital.com/assets/NPI-Add-Bulk-Deals-Template.xlsx';
export const AUTH0_CONFIG = {
  audience: import.meta.env.VITE_APP_AUTH0_AUDIENCE || '',
  clientId: import.meta.env.VITE_APP_AUTH0_CLIENT_ID || '',
  domain: import.meta.env.VITE_APP_AUTH0_DOMAIN || ''
};
export const TERMS_OF_USE =
  import.meta.env.VITE_APP_TERMS_OF_USE || 'https://www.npifinancial.com/npi-vantage-terms-of-use';
export const PRIVACY_POLICY = import.meta.env.VITE_APP_PRIVACY_POLICY || 'https://npifinancial.com/privacy-policy/';

export const scrollOptions = {
  duration: 1200,
  smooth: 'easeOutSine',
  offset: -50
};

export const dealTypes = {
  RENEWAL: 'Renewal',
  NEW_PURCHASE: 'New purchase'
};

export const dealTypeOptions = [
  { label: 'Renewals', value: dealTypes.RENEWAL },
  { label: 'New Contracts', value: dealTypes.NEW_PURCHASE }
];

export const DEFAULT_COLORS = [
  '#2f7ed8',
  '#0d233a',
  '#8bbc21',
  '#910000',
  '#1aadce',
  '#492970',
  '#f28f43',
  '#77a1e5',
  '#c42525',
  '#a6c96a'
];

export const COLOR_SCALE_FIVE_ASC = ['#7abd7e', '#8cd47e', '#f8d66d', '#ffb54c', '#ff6961'];

export const URGENCY_LOV = [
  { value: 0, label: 'Highest' },
  { value: 1, label: 'High' },
  { value: 2, label: 'Medium' },
  { value: 3, label: 'Low' },
  { value: 4, label: 'Lowest' }
];

export const URGENCY_SLIDER_VALUES = [
  { value: 0, key: 0 },
  { value: 1, key: 25 },
  { value: 2, key: 50 },
  { value: 3, key: 75 },
  { value: 4, key: 100 }
];

export const SLIDER_MARKS = {
  0: 'Low',
  25: ' ',
  50: 'Medium',
  75: ' ',
  100: 'High'
};

export const COMMUNITY_GUIDELINES = [
  'Keep online conversations professional and appropriate at all times.',
  'Refrain from promoting specific vendors or services, as this is a professional community.',
  'Keep discussions focused on the discussed topics and refrain from introducing new topics or subjects.',
  'Refrain from sharing confidential details or confidential documents without the express consent of the relevant parties.',
  'Notify a moderator or administrator if you encounter any hateful, malicious, or inappropriate behavior.',
  'Follow the instructions and guidance of the moderator team at all times.'
];

export const PINPOINT_BUDGET_STEPS: Array<PinpointStep> = [
  { value: 1, label: 'Select Supplier', completed: false },
  { value: 2, label: 'Select Product', completed: false }
];

export const PINPOINT_PRICE_CHECK_STEPS: Array<PinpointStep> = [
  { value: 1, label: 'Select Supplier', completed: false },
  { value: 2, label: 'Select Product', completed: false },
  { value: 3, label: 'Enter your quoted price, quantity and total annual contract value', completed: false }
];

export const RENEWAL_RECORD_TYPE = {
  ROOT: 'root',
  SUPPLIER_DEAL: 'supplierDeal',
  SUPPLIER: 'supplier',
  DEAL: 'deal',
  TASK: 'task',
  CHILD_ORG: 'childOrg'
};

export const DYNAMIC_COLUMNS = {
  VENDOR_COMPLEXITY: 'vendorComplexity',
  PRICING_FLEXIBILITY: 'pricingFlexibility',
  DEAL_SIZE_TREND: 'dealSizeTrend'
};
export const DEAL_DOCUMENTS = {
  PREVIEW_NOT_AVAILABLE: 'Preview not available',
  PLEASE_DOWNLOAD_THE_FILE_HERE: 'Please download the file here',
  UPLOADED_DOCUMENTS: 'Uploaded Documents',
  UPLOAD_DOCUMENTS: 'Upload Documents',
  PLEASE_SELECT_ANY_FILE_TO_PREVIEW: 'Please select any file to Preview'
};

export const PURCHASE_CHARACTERISTICS_THREE_COLORS = ['#a1cd6c', '#ffc106', '#ee7267'];
export const PURCHASE_CHARACTERISTICS_VALUE = ['Low', 'Medium', 'High'];
export const PURCHASE_CHARACTERISTICS_EIGHT_COLORS = [
  '#6d73c7',
  '#57a89e',
  '#abcb79',
  '#f8d354',
  '#f8d354',
  '#f19e52',
  '#ed7267',
  '#8f4341'
];

export const DEAL_SIZE_TREND_CHARACTERISTICS = {
  TRENDING_DOWNWARDS_TEXT: 'Trending Downwards',
  STABLE_TEXT: 'Stable',
  TRENDING_UPWARDS_TEXT: 'Trending Upwards'
};

export const RENEWAL_ZONE_CONTENT =
  'The "Red Zone" is a critical period when an IT contract is nearing its renewal date, indicating that organizations should be taking immediate action to ensure favorable terms and timely closure. The "Orange Zone" signifies an earlier phase, indicating that a contract renewal is approaching, giving organizations a heads-up to start preparing and strategizing well in advance.';

export const ckeditorToolbarOptions = [
  'heading',
  '|',
  'fontSize',
  'fontFamily',
  '|',
  'fontColor',
  'fontBackgroundColor',
  '|',
  'bold',
  'italic',
  'underline',
  '|',
  'textAlignment',
  '|',
  'numberedList',
  'bulletedList',
  '|',
  'decreaseIndent',
  'increaseIndent',
  '|',
  'link',
  'blockQuote',
  'insertTable',
  '|',
  'undo',
  'redo'
];
export const TRUSTED_PARTNER = import.meta.env.VITE_APP_TRUSTED_PARTNER ?? 'Plus';
export const DEFAULT_SUBSCRIPTION_PURCHASE_COLUMN_SETTINGS = {
  Primary_Vendor_not_clickable__c: true,
  Subject__c: true,
  Start_Date__c: true,
  Client__r: true,
  Deal_Status__c: true,
  Deal_Reason__c: true,
  Date_Status_Findings_Provided__c: true,
  Term__c: true,
  Initial_Quote_Full_Contract_Term__c: true,
  NPI_Savings_Target_Full_Contract_Term__c: true,
  Total_Savings_3yr__c: true,
  Report_Type__c: true,
  FMV_Report_Link__c: true,
  Contract_Renewal_Date__c: true,
  category: true,
  tags: true,
  tcv: false,
  reseller: false,
  Date_Time_Closed__c: false
};

export const UNIT_OPTIONS = [
  { label: 'Second', value: 'Second' },
  { label: 'Minute', value: 'Minute' },
  { label: 'Hour', value: 'Hour' },
  { label: 'Day', value: 'Day' }
];

export const METHOD_STYLES: Record<HttpMethod, string> = {
  GET: 'bg-npi-blue text-white',
  POST: 'bg-green-500 text-white',
  PATCH: 'bg-cyan-400 text-white',
  PUT: 'bg-amber-500 text-white',
  DELETE: 'bg-red-500 text-white'
};
